import axios from "axios";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { Button } from "@/components/ui/button/Button";
import Drawer from "@/components/ui/drawer";
import { CC_ERROR_TEXT, CC_FIELD, CC_INSET, CC_PANEL, CC_PANEL_FLUSH, CC_SECTION_LABEL } from "@/components/case-checking/caseCheckingChrome";
import { CaseCheckingListRow } from "@/components/case-checking/CaseCheckingListRow";
import {
  agentStatusToHumanCall,
  normalizeRuleStatus,
  type RuleHumanCall,
} from "@/components/case-checking/problemStatementRuleIssues";
import { AgentAssessmentSection } from "@/components/case-checking/agentAssessmentDisplay";
import {
  compareProblemStatementIds,
  groupByGateway,
  ruleDisplayName,
  ruleLabelBreadcrumb,
  ruleRowTitle,
  sortRulesForDisplay,
} from "@/components/case-checking/problemStatementRuleDisplay";
import {
  effectiveRuleStatus,
  applyRuleOverridesToRollup,
  isSavedHumanRuleReview,
  mergeHumanRuleReviews,
  normalizePsRuleHumanReview,
  normalizePsRuleHumanReviewRun,
  ruleStatusToApiString,
} from "@/components/case-checking/humanRuleReviewCascade";
import { pillLabelForRating, RATING_TILE_CLASS } from "@/components/case-checking/problemStatementUi";
import { StatementSupportingDocs } from "@/components/case-checking/StatementSupportingDocs";
import { useCaseDetailData } from "@/components/case-checking/CaseDetailDataContext";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import {
  getActivePsRuleHumanReviewRun,
  getCaseDocuments,
  patchPsRuleHumanReviews,
  type CaseDocument,
  type ProblemStatementRating,
  type ProblemStatementRollup,
  type ProblemStatementRuleSnapshot,
  type PsRuleHumanReview,
  type PsRuleHumanReviewPatch,
  type PsRuleHumanReviewRun,
} from "@/services/api/case-checking";

type RuleColorFilter = "green" | "amber" | "red";
type RuleOutcomeFilter = "all" | RuleColorFilter;

const ALL_RULE_COLOR_FILTERS = new Set<RuleColorFilter>(["red", "amber", "green"]);

const RULE_CALL_OPTIONS: { id: RuleHumanCall; label: string }[] = [
  { id: "pass", label: "Pass" },
  { id: "fail", label: "Fail" },
  { id: "unclear", label: "Unclear" },
  { id: "na", label: "N/A" },
  { id: "excluded", label: "Exclude" },
];

const CALL_LABEL: Record<RuleHumanCall, string> = {
  pass: "Pass",
  fail: "Fail",
  unclear: "Unclear",
  na: "N/A",
  excluded: "Excluded",
};

const CALL_TONE: Record<RuleHumanCall, string> = {
  pass: RATING_TILE_CLASS.pass,
  fail: RATING_TILE_CLASS.fail,
  unclear: RATING_TILE_CLASS.unclear,
  na: RATING_TILE_CLASS.na,
  excluded: "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:ring-slate-500/25",
};

const STATUS_DOT: Record<string, string> = {
  passed: "bg-emerald-400",
  failed: "bg-rose-400",
  unclear: "bg-amber-400",
  na: "bg-slate-500",
};

function reviewKey(problemStatementId: string, ruleId: string): string {
  return `${problemStatementId}::${ruleId}`;
}

function ruleColorBucket(status: string): RuleColorFilter | null {
  const normalized = normalizeRuleStatus(status);
  if (normalized === "passed") return "green";
  if (normalized === "unclear") return "amber";
  if (normalized === "failed") return "red";
  return null;
}

function ruleMatchesOutcomeFilter(status: string, filter: RuleOutcomeFilter): boolean {
  if (filter === "all") return true;
  const bucket = ruleColorBucket(status);
  if (!bucket) return false;
  return bucket === filter;
}

function isRuleVisible(
  rule: ProblemStatementRuleSnapshot,
  rollupId: string,
  outcomeFilter: RuleOutcomeFilter,
  overridesByKey: Map<string, PsRuleHumanReview>,
): boolean {
  const key = reviewKey(rollupId, rule.rule_id);
  const override = overridesByKey.get(key);
  if (override && isSavedHumanRuleReview(override)) return true;
  const effective = ruleStatusToApiString(effectiveRuleStatus(rule.status, override));
  return ruleMatchesOutcomeFilter(effective, outcomeFilter);
}

type VerdictCounts = Record<RuleColorFilter, number>;

function countEffectiveVerdicts(
  rules: ProblemStatementRuleSnapshot[],
  rollupId: string,
  overridesByKey: Map<string, PsRuleHumanReview>,
): VerdictCounts {
  const counts: VerdictCounts = { red: 0, amber: 0, green: 0 };
  for (const rule of rules) {
    const override = overridesByKey.get(reviewKey(rollupId, rule.rule_id));
    const effective = ruleStatusToApiString(effectiveRuleStatus(rule.status, override));
    const bucket = ruleColorBucket(effective);
    if (bucket) counts[bucket] += 1;
  }
  return counts;
}

function VerdictChip({ call }: { call: RuleHumanCall }) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        CALL_TONE[call],
      )}
    >
      {CALL_LABEL[call]}
    </span>
  );
}

function EditedBadge({ count }: { count?: number }) {
  const label = count && count > 1 ? `${count} edited` : "Edited";
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full bg-indigo-500/15 px-2 py-0.5 text-[11px] font-medium text-indigo-800 ring-1 ring-indigo-500/30 dark:text-indigo-200"
      data-testid="rule-edited-badge"
    >
      {label}
    </span>
  );
}

function RowVerdictLabel({
  agentCall,
  hasChange,
}: {
  agentCall: RuleHumanCall;
  humanCall: RuleHumanCall;
  hasChange: boolean;
}) {
  if (hasChange) return <EditedBadge />;
  return <span className="shrink-0 text-xs text-slate-500">{CALL_LABEL[agentCall]}</span>;
}

function SectionCountBadges({
  counts,
  showAll = false,
  activeFilters,
}: {
  counts: VerdictCounts;
  /** Section headers show all RAG counts; top filter only shows active buckets. */
  showAll?: boolean;
  activeFilters: Set<RuleColorFilter>;
}) {
  const items: { key: RuleColorFilter; label: string; dot: string }[] = [];
  const include = (key: RuleColorFilter) => showAll || activeFilters.has(key);
  if (include("red") && counts.red > 0) {
    items.push({ key: "red", label: `${counts.red} failed`, dot: "bg-rose-400" });
  }
  if (include("amber") && counts.amber > 0) {
    items.push({ key: "amber", label: `${counts.amber} unclear`, dot: "bg-amber-400" });
  }
  if (include("green") && counts.green > 0) {
    items.push({ key: "green", label: `${counts.green} passed`, dot: "bg-emerald-400" });
  }
  if (items.length === 0) return null;
  return (
    <span className="flex shrink-0 flex-wrap items-center gap-x-2 text-xs tabular-nums text-slate-500">
      {items.map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1.5">
          <span className={clsx("h-1.5 w-1.5 rounded-full", item.dot)} aria-hidden />
          {item.label}
        </span>
      ))}
    </span>
  );
}

function PsRagPill({
  rating,
  edited,
}: {
  rating: ProblemStatementRating;
  edited?: boolean;
}) {
  const label = pillLabelForRating(rating);
  if (!label || label === "—") return null;
  const tone = RATING_TILE_CLASS[rating] ?? RATING_TILE_CLASS.na;
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        tone,
      )}
      data-testid="ps-rag-pill"
    >
      {label}
      {edited ? <span className="ml-1 text-indigo-300/80">·</span> : null}
    </span>
  );
}

function SectionEditHint({ humanChangeCount }: { humanChangeCount: number }) {
  if (humanChangeCount === 0) return null;
  return <EditedBadge count={humanChangeCount} />;
}

function RuleOutcomeFilterBar({
  value,
  onChange,
}: {
  value: RuleOutcomeFilter;
  onChange: (next: RuleOutcomeFilter) => void;
}) {
  const options: { id: RuleOutcomeFilter; label: string; dot?: string }[] = [
    { id: "all", label: "All" },
    { id: "red", label: "Failed", dot: "bg-rose-400" },
    { id: "amber", label: "Unclear", dot: "bg-amber-400" },
    { id: "green", label: "Passed", dot: "bg-emerald-400" },
  ];

  return (
    <div
      role="group"
      aria-label="Filter rules by outcome"
      className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-900/40 p-1 ring-1 ring-slate-200 dark:ring-white/5"
    >
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <Button
            key={opt.id}
            plain
            type="button"
            onClick={() => onChange(opt.id)}
            aria-pressed={active}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
              active ? "bg-slate-100 dark:bg-white/[0.08] text-slate-900 dark:text-slate-100 ring-1 ring-slate-200 dark:ring-white/10" : "text-slate-400 hover:text-slate-700 dark:hover:text-slate-200",
            )}
            data-testid={`rules-filter-${opt.id}`}
          >
            {opt.dot ? <span className={clsx("h-1.5 w-1.5 rounded-full", opt.dot)} aria-hidden /> : null}
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

function RuleCallPills({
  value,
  onChange,
  disabled,
}: {
  value: RuleHumanCall;
  onChange: (next: RuleHumanCall) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Rule verdict" className="flex flex-wrap gap-1.5">
      {RULE_CALL_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <Button
            key={opt.id}
            plain
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            role="radio"
            aria-checked={active}
            className={clsx(
              "rounded-md px-2.5 py-1.5 text-xs font-medium ring-1 ring-inset transition-colors",
              active
                ? "bg-indigo-500/15 text-indigo-100 ring-indigo-500/35"
                : "bg-slate-50 dark:bg-white/[0.03] text-slate-400 ring-slate-200 dark:ring-white/10 hover:text-slate-700 dark:hover:text-slate-200",
            )}
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

type ActiveRule = {
  problemStatementId: string;
  sectionTitle: string;
  rule: ProblemStatementRuleSnapshot;
};

function RuleReviewDrawer({
  active,
  caseId,
  override,
  onClose,
  onSave,
  saving,
  documents,
  documentsLoading,
}: {
  active: ActiveRule | null;
  caseId: string;
  override?: PsRuleHumanReview;
  onClose: () => void;
  onSave: (patch: PsRuleHumanReviewPatch) => void;
  saving: boolean;
  documents: CaseDocument[];
  documentsLoading: boolean;
}) {
  const rule = active?.rule;
  const agentCall = rule ? agentStatusToHumanCall(rule.status) : "unclear";
  const hasChange = isSavedHumanRuleReview(override);
  const humanCall = hasChange ? override!.call : agentCall;
  const [draftCall, setDraftCall] = useState<RuleHumanCall>(humanCall);
  const [draftNote, setDraftNote] = useState(override?.notes ?? "");

  useEffect(() => {
    if (!rule) return;
    setDraftCall(hasChange ? override!.call : agentCall);
    setDraftNote(override?.notes ?? "");
  }, [rule, hasChange, override, agentCall]);

  if (!active || !rule) return null;

  const evidence = rule.evidence?.trim() ?? "";
  const label = rule.label?.trim() ?? "";
  const breadcrumb = ruleLabelBreadcrumb(label);
  const drawerTitle = ruleDisplayName(rule);

  const save = () => {
    const note = draftNote.trim();
    if (draftCall === agentCall && !note) {
      onSave({
        problem_statement_id: active.problemStatementId,
        rule_id: rule.rule_id,
        call: agentCall,
        notes: "",
        reviewer_name: "",
        reviewed_at: null,
        clear: true,
      });
      return;
    }
    onSave({
      problem_statement_id: active.problemStatementId,
      rule_id: rule.rule_id,
      call: draftCall,
      notes: note,
      reviewer_name: "",
      reviewed_at: null,
    });
  };

  const revert = () => {
    onSave({
      problem_statement_id: active.problemStatementId,
      rule_id: rule.rule_id,
      call: agentCall,
      notes: "",
      reviewer_name: "",
      reviewed_at: null,
      clear: true,
    });
    setDraftCall(agentCall);
    setDraftNote("");
  };

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={drawerTitle}
      width="max-w-xl"
    >
      <div className="space-y-5 px-6 pb-8 pt-1">
        <p className="text-xs text-slate-500">
          <span className="font-mono">{active.problemStatementId}</span>
          <span className="mx-1.5 text-slate-600">·</span>
          <span className="font-mono">{rule.rule_id}</span>
          {breadcrumb ? (
            <>
              <span className="mx-1.5 text-slate-600">·</span>
              <span>{breadcrumb}</span>
            </>
          ) : null}
        </p>

        <section>
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Agent assessment</p>
              <p className="text-[11px] text-slate-600">Why the agent gave this verdict</p>
            </div>
            <VerdictChip call={agentCall} />
          </div>
          <AgentAssessmentSection evidence={evidence} />
        </section>

        <StatementSupportingDocs
          caseId={caseId}
          rules={[rule]}
          documents={documents}
          documentsLoading={documentsLoading}
          compact
        />

        {hasChange ? (
          <div
            className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200"
            data-testid="rule-human-override-banner"
          >
            <EditedBadge />
            <span className="text-slate-500 dark:text-slate-400">Agent</span>
            <VerdictChip call={agentCall} />
            <span className="text-slate-500">→</span>
            <span className="text-slate-500 dark:text-slate-400">You</span>
            <VerdictChip call={humanCall} />
          </div>
        ) : null}

        <section
          className={clsx(
            CC_INSET,
            "p-4 ring-1",
            hasChange || draftCall !== agentCall
              ? "ring-indigo-500/40 bg-indigo-500/[0.06] dark:ring-indigo-400/35"
              : "ring-slate-200 dark:ring-white/10",
          )}
        >
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Your verdict</p>
          <RuleCallPills value={draftCall} onChange={setDraftCall} disabled={saving} />
          <label htmlFor="rule-review-override-notes" className="sr-only">
            Notes for your rule verdict
          </label>
          <textarea
            id="rule-review-override-notes"
            className={clsx(CC_FIELD, "mt-3 min-h-[5rem] resize-none rounded-md")}
            rows={3}
            placeholder={
              draftCall === "excluded"
                ? "Why doesn't this rule apply?"
                : draftCall !== agentCall
                  ? "Why are you changing this?"
                  : "Optional note"
            }
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value)}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button color="indigo" onClick={save} disabled={saving}>
              {saving ? "Saving…" : hasChange ? "Update" : "Save"}
            </Button>
            {hasChange ? (
              <Button plain size="unstyled" onClick={revert} disabled={saving}>
                Revert to agent
              </Button>
            ) : null}
            <Button plain onClick={onClose} disabled={saving}>
              Close
            </Button>
          </div>
        </section>
      </div>
    </Drawer>
  );
}

function RuleReviewRow({
  rule,
  override,
  onOpen,
}: {
  rule: ProblemStatementRuleSnapshot;
  override?: PsRuleHumanReview;
  onOpen: () => void;
}) {
  const agentCall = agentStatusToHumanCall(rule.status);
  const hasChange = isSavedHumanRuleReview(override);
  const humanCall = hasChange ? override!.call : agentCall;
  const rowTitle = ruleRowTitle(rule);
  const effectiveStatus = effectiveRuleStatus(rule.status, override);
  const dotClass = STATUS_DOT[effectiveStatus] ?? "bg-slate-600";

  return (
    <CaseCheckingListRow
      variant="flush"
      onClick={onOpen}
      ariaLabel={`Review rule ${rowTitle}`}
      testId={`rule-review-${rule.rule_id}`}
      innerClassName="items-center py-2.5"
    >
      <span className={clsx("h-1.5 w-1.5 shrink-0 rounded-full", dotClass)} aria-hidden />
      <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{rowTitle}</span>
      <RowVerdictLabel agentCall={agentCall} humanCall={humanCall} hasChange={hasChange} />
      <span className="shrink-0 text-[11px] font-medium text-slate-500">{rule.rule_id}</span>
      <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 shrink-0 text-slate-600" />
    </CaseCheckingListRow>
  );
}

function RuleSection({
  rollup,
  outcomeFilter,
  overridesByKey,
  onSelectRule,
}: {
  rollup: ProblemStatementRollup;
  outcomeFilter: RuleOutcomeFilter;
  overridesByKey: Map<string, PsRuleHumanReview>;
  onSelectRule: (active: ActiveRule) => void;
}) {
  const visibleRules = useMemo(
    () =>
      sortRulesForDisplay(rollup.rules).filter((rule) =>
        isRuleVisible(rule, rollup.id, outcomeFilter, overridesByKey),
      ),
    [rollup.rules, rollup.id, outcomeFilter, overridesByKey],
  );

  const { rollup: effectiveRollup, effectiveRules } = useMemo(
    () => applyRuleOverridesToRollup(rollup, overridesByKey),
    [rollup, overridesByKey],
  );

  const verdictCounts = useMemo(
    () => countEffectiveVerdicts(visibleRules, rollup.id, overridesByKey),
    [visibleRules, rollup.id, overridesByKey],
  );

  const humanChangeCount = useMemo(
    () => effectiveRules.filter((r) => r.humanChanged).length,
    [effectiveRules],
  );
  const ratingChanged = effectiveRollup.rating !== rollup.rating;
  const [expanded, setExpanded] = useState(false);

  if (rollup.rules.length === 0 || visibleRules.length === 0) return null;

  const sectionFooter = expanded ? (
    <div className="divide-y divide-slate-200 border-t border-slate-200 dark:divide-white/5 dark:border-white/5">
      {visibleRules.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-500">No rules match the current filters.</p>
      ) : (
        visibleRules.map((rule) => {
          const key = reviewKey(rollup.id, rule.rule_id);
          return (
            <RuleReviewRow
              key={key}
              rule={rule}
              override={overridesByKey.get(key)}
              onOpen={() =>
                onSelectRule({
                  problemStatementId: rollup.id,
                  sectionTitle: rollup.title,
                  rule,
                })
              }
            />
          );
        })
      )}
    </div>
  ) : null;

  return (
    <div className={CC_PANEL_FLUSH} data-testid={`rules-section-${rollup.id}`}>
      <CaseCheckingListRow
        variant="flush"
        onClick={() => setExpanded((v) => !v)}
        ariaExpanded={expanded}
        ariaLabel={`${rollup.title} rules, ${expanded ? "collapse" : "expand"} section`}
        innerClassName="items-center"
        footer={sectionFooter}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
            <span className="font-mono text-xs text-slate-500">{rollup.id}</span>
            <span className="mx-1.5 text-slate-700">·</span>
            <span>{rollup.title}</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <PsRagPill rating={effectiveRollup.rating} edited={ratingChanged} />
          <SectionCountBadges
            counts={verdictCounts}
            showAll={outcomeFilter === "all"}
            activeFilters={outcomeFilter === "all" ? ALL_RULE_COLOR_FILTERS : new Set([outcomeFilter])}
          />
          <SectionEditHint humanChangeCount={humanChangeCount} />
        </div>
        <FontAwesomeIcon
          icon={faChevronRight}
          className={clsx("h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform", expanded && "rotate-90")}
        />
      </CaseCheckingListRow>
    </div>
  );
}

export function ReviewProblemStatementRulesStep({
  caseId,
  reviewSessionId,
  reviewRun: reviewRunProp,
  onReviewRunChange,
  caseDocuments,
  caseDocumentsLoading,
}: {
  caseId: string;
  reviewSessionId?: string;
  reviewRun?: PsRuleHumanReviewRun | null;
  onReviewRunChange?: (run: PsRuleHumanReviewRun) => void;
  caseDocuments?: CaseDocument[];
  caseDocumentsLoading?: boolean;
}) {
  const shared = useCaseDetailData();
  const sharedCaseId = shared?.caseId;
  const sharedPsRuleReviewRun = shared?.psRuleReviewRun;
  const sharedPsRuleReviewLoading = shared?.psRuleReviewLoading;
  const sharedPsRuleReviewError = shared?.psRuleReviewError;
  const setSharedPsRuleReviewRun = shared?.setPsRuleReviewRun;
  const { run, loading, error, scorable } = useProblemStatementRun(caseId);
  const [reviewRun, setReviewRun] = useState<PsRuleHumanReviewRun | null>(reviewRunProp ?? null);
  const [reviewLoading, setReviewLoading] = useState(!reviewRunProp);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [outcomeFilter, setOutcomeFilter] = useState<RuleOutcomeFilter>("all");
  const [saveState, setSaveState] = useState<Record<string, "idle" | "saving" | "saved">>({});
  const [documents, setDocuments] = useState<CaseDocument[]>(caseDocuments ?? []);
  const [documentsLoading, setDocumentsLoading] = useState(
    caseDocuments === undefined && caseDocumentsLoading !== true,
  );
  const [activeRule, setActiveRule] = useState<ActiveRule | null>(null);

  useEffect(() => {
    if (caseDocuments !== undefined) {
      setDocuments(caseDocuments);
      setDocumentsLoading(caseDocumentsLoading ?? false);
      return;
    }
    let cancelled = false;
    setDocumentsLoading(true);
    getCaseDocuments(caseId)
      .then((docs) => {
        if (!cancelled) setDocuments(docs ?? []);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseDocuments, caseDocumentsLoading, caseId]);

  useEffect(() => {
    if (reviewRunProp) {
      setReviewRun((prev) => {
        const incoming = normalizePsRuleHumanReviewRun(reviewRunProp);
        if (!prev || prev.id !== incoming.id) return incoming;
        return {
          ...incoming,
          reviews: mergeHumanRuleReviews(prev.reviews ?? [], incoming.reviews ?? []),
        };
      });
      setReviewLoading(false);
      setReviewError(null);
      return;
    }
    if (
      sharedCaseId === caseId &&
      sharedPsRuleReviewRun &&
      (!reviewSessionId || sharedPsRuleReviewRun.id === reviewSessionId)
    ) {
      setReviewRun(sharedPsRuleReviewRun);
      setReviewLoading(sharedPsRuleReviewLoading ?? false);
      setReviewError(sharedPsRuleReviewError ?? null);
      return;
    }
    if (!run?.id) {
      setReviewRun(null);
      setReviewLoading(false);
      return;
    }
    let cancelled = false;
    setReviewLoading(true);
    setReviewError(null);
    getActivePsRuleHumanReviewRun(caseId, run.id)
      .then((active) => {
        if (!cancelled) setReviewRun(active);
      })
      .catch((e) => {
        if (!cancelled) setReviewError(e instanceof Error ? e.message : "Failed to load rules");
      })
      .finally(() => {
        if (!cancelled) setReviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    caseId,
    reviewRunProp,
    reviewSessionId,
    run?.id,
    sharedCaseId,
    sharedPsRuleReviewRun,
    sharedPsRuleReviewLoading,
    sharedPsRuleReviewError,
  ]);

  const overridesByKey = useMemo(() => {
    const map = new Map<string, PsRuleHumanReview>();
    for (const raw of reviewRun?.reviews ?? []) {
      const review = normalizePsRuleHumanReview(raw);
      map.set(reviewKey(review.problem_statement_id, review.rule_id), review);
    }
    return map;
  }, [reviewRun?.reviews]);

  const orderedSections = useMemo(
    () => [...scorable].sort((a, b) => compareProblemStatementIds(a.id, b.id)),
    [scorable],
  );

  const overrideCount = reviewRun?.reviews.filter(isSavedHumanRuleReview).length ?? 0;

  const activeOverride = activeRule
    ? overridesByKey.get(reviewKey(activeRule.problemStatementId, activeRule.rule.rule_id))
    : undefined;
  const activeSaving = activeRule
    ? saveState[reviewKey(activeRule.problemStatementId, activeRule.rule.rule_id)] === "saving"
    : false;

  const saveOverride = useCallback(
    async (patch: PsRuleHumanReviewPatch) => {
      if (!reviewRun) return;
      const key = reviewKey(patch.problem_statement_id, patch.rule_id);
      setSaveState((s) => ({ ...s, [key]: "saving" }));
      try {
        const updated = await patchPsRuleHumanReviews(caseId, reviewRun.id, [patch]);
        const normalized = normalizePsRuleHumanReviewRun(updated);
        const merged = {
          ...normalized,
          reviews: mergeHumanRuleReviews(reviewRun.reviews ?? [], normalized.reviews ?? []),
        };
        setReviewRun(merged);
        if (onReviewRunChange) onReviewRunChange(merged);
        else if (setSharedPsRuleReviewRun) setSharedPsRuleReviewRun(updated);
        setSaveState((s) => ({ ...s, [key]: "saved" }));
        window.setTimeout(() => {
          setSaveState((s) => (s[key] === "saved" ? { ...s, [key]: "idle" } : s));
        }, 1500);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404 && shared) {
          setSaveState((s) => ({ ...s, [key]: "idle" }));
          void shared.reloadActiveReview();
          return;
        }
        setReviewError(e instanceof Error ? e.message : "Failed to save");
        setSaveState((s) => ({ ...s, [key]: "idle" }));
      }
    },
    [caseId, onReviewRunChange, reviewRun, setSharedPsRuleReviewRun, shared],
  );

  if (loading || reviewLoading) {
    return <p className="text-sm text-slate-400">Loading rules…</p>;
  }

  if (error || !run) {
    return (
      <div className={CC_PANEL}>
        <p className="text-sm text-slate-600 dark:text-slate-300">{error ?? "No rules available yet."}</p>
      </div>
    );
  }

  if (!reviewRun) {
    return (
      <div className={CC_PANEL}>
        <p className="text-sm text-rose-300">{reviewError ?? "Could not load review session."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="review-rules-step">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {overrideCount > 0 ? (
          <span className="text-xs text-slate-500">
            {overrideCount} change{overrideCount === 1 ? "" : "s"} saved
          </span>
        ) : (
          <span />
        )}
        <RuleOutcomeFilterBar value={outcomeFilter} onChange={setOutcomeFilter} />
      </div>

      {reviewError ? <p className={clsx("text-xs", CC_ERROR_TEXT)}>{reviewError}</p> : null}

      <div className="space-y-4">
        {groupByGateway(orderedSections).map((group) => (
          <div key={group.gateway ?? "all"} className="space-y-2">
            {group.label ? <p className={clsx(CC_SECTION_LABEL, "!mb-0")}>{group.label}</p> : null}
            {group.items.map((rollup) => (
              <RuleSection
                key={rollup.id}
                rollup={rollup}
                outcomeFilter={outcomeFilter}
                overridesByKey={overridesByKey}
                onSelectRule={setActiveRule}
              />
            ))}
          </div>
        ))}
      </div>

      <RuleReviewDrawer
        active={activeRule}
        caseId={caseId}
        override={activeOverride}
        onClose={() => setActiveRule(null)}
        onSave={(patch) => void saveOverride(patch)}
        saving={activeSaving}
        documents={documents}
        documentsLoading={documentsLoading}
      />
    </div>
  );
}
